import React, {
    useCallback, useEffect, useMemo, useState
} from 'react'
import styled from 'styled-components'
import Button from 'components/Button'
import DropdownButton from 'components/DropdownButton'
import { _ } from 'utils'
import colors, { $gray_600 } from 'utils/colors'

type ItemsPerPageOptionProps = {
    option: number
    onSelected: (option: number) => void
}

const ItemsPerPageOption = ({ option, onSelected }: ItemsPerPageOptionProps) => {
    const handleClick = useCallback(() => {
        onSelected(option)
    }, [onSelected, option])
    return (
        <div onClick={handleClick}>
            {option}
        </div>
    )
}

const S = {
    Container: styled.div`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-top: 20px;
    `,
    DropdownButton: styled(DropdownButton)`
        margin-right: 60px;
        
        * {
            color: ${$gray_600} !important;
        }
    `,
    CurrentItemRange: styled.div`
        margin-right: 20px;
        color: ${$gray_600};
    `,
    NavButtonContainer: styled.div`
        white-space: nowrap;
    `,
    NavButton: styled(Button)`
        background: transparent;
        border: 0;
        border-radius: 0;
        padding: 0;
        width: 30px;
        height: 30px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin-left: 10px;
    `
}

type Props = {
    totalItems: number
    onPageChange: (page: number, itemsPerPage: number) => Promise<boolean>
    itemsPerPage: number
    itemsPerPageOpts: number[]
    disabled?: boolean
    currentPage?: number
}

/**
 * Component to render UI for pagination. Not handle API calls.
 */
const BasePagination = (props: Props) => {
    const {
        totalItems, onPageChange, disabled, itemsPerPageOpts
    } = props
    const [itemsPerPage, setItemsPerPage] = useState(props.itemsPerPage)
    const [currentPage, setCurrentPage] = useState(props.currentPage || 1)

    const totalPages = useMemo(() => Math.ceil(totalItems / itemsPerPage), [totalItems, itemsPerPage])

    const handlePrevPage = useCallback(async () => {
        if (currentPage <= 1) {
            return
        }

        const newPage = currentPage - 1
        if (await onPageChange(newPage, itemsPerPage)) {
            setCurrentPage(newPage)
        }
    }, [currentPage, itemsPerPage, onPageChange])

    const handleNextPage = useCallback(async () => {
        if (currentPage >= totalPages) {
            return
        }

        const newPage = currentPage + 1
        if (await onPageChange(newPage, itemsPerPage)) {
            setCurrentPage(newPage)
        }
    }, [currentPage, itemsPerPage, onPageChange, totalPages])

    const handleChangeItemsPerPage = useCallback(async (opt) => {
        if (await onPageChange(1, opt)) {
            setCurrentPage(1)
            setItemsPerPage(opt)
        }
    }, [onPageChange])

    useEffect(() => {
        setCurrentPage(props.currentPage || 1)
    }, [props.currentPage])

    const startItemNumber = useMemo(() => (!totalItems ? 0 : (currentPage - 1) * itemsPerPage + 1),
        [currentPage, itemsPerPage, totalItems])
    const endItemNumber = useMemo(() => {
        const end = (currentPage) * itemsPerPage + 1
        return end > totalItems ? totalItems : end
    }, [currentPage, itemsPerPage, totalItems])

    return (
        <S.Container>
            <S.DropdownButton
                name={_('pagination.rows_per_page')}
                variant="light"
                value={itemsPerPage}
                direction="up"
            >
                {itemsPerPageOpts.map((opt) => (
                    <ItemsPerPageOption key={opt} option={opt} onSelected={handleChangeItemsPerPage} />
                ))}
            </S.DropdownButton>
            <S.CurrentItemRange>
                {`${startItemNumber} - ${endItemNumber} ${_('pagination.of', {}, false)} ${totalItems}`}
            </S.CurrentItemRange>
            <S.NavButtonContainer>
                <S.NavButton
                    icon="previous"
                    onClick={handlePrevPage}
                    disabled={disabled || currentPage <= 1}
                    iconProps={{ color: colors.icon, size: 30, style: { marginBottom: 2 } }}
                />
                <S.NavButton
                    icon="next"
                    onClick={handleNextPage}
                    disabled={disabled || currentPage >= totalPages}
                    iconProps={{ color: colors.icon, size: 30, style: { marginBottom: 2 } }}
                />
            </S.NavButtonContainer>
        </S.Container>
    )
}

export default BasePagination
